import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Page } from '@/shared/ui/Page';
import { BottomDock } from '@/shared/ui/BottomDock';
import { useAppStore } from '@/shared/store/appStore';
import { subscriptionApi } from '@/entities/subscription/api';
import { ledgerApi } from '@/entities/ledger-operation/api';
import { paymentApi } from '@/entities/payment-claim/api';
import { SubscriptionCardHolder } from '@/entities/subscription/SubscriptionCardHolder';
import { SubscriptionCardMember } from '@/entities/subscription/SubscriptionCardMember';
import { DraftCard } from '@/entities/subscription/DraftCard';
import { MarkPaidSheet } from '@/features/mark-paid/MarkPaidSheet';
import { PaymentConfirmation } from '@/features/payment-confirmation/PaymentConfirmation';
import { GlassCard } from '@/shared/ui/GlassCard';
import { MonoAmount } from '@/shared/ui/MonoAmount';
import { SectionLabel } from '@/shared/ui/SectionLabel';
import { SubscriptionIcon } from '@/shared/ui/SubscriptionIcon';
import { getCurrentUser } from '@/shared/api/auth';
import { cn } from '@/shared/lib/cn';
import { useI18nStore, useT } from '@/shared/lib/i18n';
import type { PaymentClaim, RecordPaymentResult } from '@/entities/payment-claim/types';
import type { Subscription, SubscriptionDetail } from '@/entities/subscription/types';

type LocalPaymentState = 'paid' | 'pending';
type PendingPaymentAttention = {
  claim: PaymentClaim;
  subscriptionName: string;
};

const CURRENCY_SYMBOL: Record<string, string> = { RUB: '₽', USD: '$', EUR: '€' };

function amountText(value: number, currency: string) {
  return `${Math.max(0, value).toLocaleString('ru')} ${CURRENCY_SYMBOL[currency] ?? currency}`;
}

function toNumber(value: number | string | null | undefined) {
  if (value === null || value === undefined) return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function estimatedMemberShare(sub: Subscription) {
  const members = Math.max(1, sub.member_count || 1);
  return Number(sub.total_price) / members;
}

function resolveAmountDue(
  sub: Subscription,
  detail?: SubscriptionDetail,
  localState?: LocalPaymentState,
) {
  if (sub.status === 'draft') return 0;
  if (localState === 'paid' || localState === 'pending') return 0;
  const fromList = toNumber(sub.balance);
  const fromDetail = toNumber(detail?.viewer_balance);
  return Math.max(0, fromList ?? fromDetail ?? estimatedMemberShare(sub));
}

function resolveMemberStatus(
  sub: Subscription,
  amountDue: number,
  localState?: LocalPaymentState,
): 'paid' | 'due' | 'overdue' | 'pending' | 'draft' {
  if (sub.status === 'draft') return 'draft';
  if (localState === 'pending') return 'pending';
  if (localState === 'paid' || amountDue <= 0) return 'paid';

  const deadline = sub.deadline ?? sub.next_billing_date ?? sub.billing_date;
  if (deadline) {
    const date = new Date(deadline);
    if (!Number.isNaN(date.getTime()) && date < new Date()) return 'overdue';
  }
  return 'due';
}

function nearestDeadline(subs: Subscription[], lang: 'ru' | 'en') {
  const dates = subs
    .map((s) => s.deadline ?? s.next_billing_date ?? s.billing_date)
    .filter((value): value is string => Boolean(value))
    .map((value) => new Date(value))
    .filter((value) => !Number.isNaN(value.getTime()))
    .sort((a, b) => a.getTime() - b.getTime());

  if (dates.length === 0) return null;
  return dates[0].toLocaleDateString(lang, { day: 'numeric', month: 'short' });
}

function paymentClaimIdFromPayload(payload: Record<string, unknown> | null) {
  const value = payload?.payment_claim_id;
  return typeof value === 'string' && value.length > 0 ? value : null;
}

// ─── Role Toggle ──────────────────────────────────────────────────────────────

function PersonIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4 4-6 8-6s8 2 8 6" />
    </svg>
  );
}

function CrownIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m2 18 2-10 5 4 3-7 3 7 5-4 2 10z" />
      <path d="M2 18h20" />
    </svg>
  );
}

function RoleToggle() {
  const { activeRole, setRole } = useAppStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const t = useT();
  return (
    <div className="flex items-center gap-[2px] rounded-full bg-black/[0.06] p-[4px] flex-shrink-0">
      {(['member', 'holder'] as const).map((r) => (
        <button
          key={r}
          onClick={() => {
            setRole(r);
            const next = new URLSearchParams(searchParams);
            next.set('role', r);
            setSearchParams(next, { replace: true });
          }}
          aria-label={r === 'member' ? t('home.roles.member') : t('home.roles.holder')}
          className={cn(
            'w-9 h-9 rounded-full flex items-center justify-center transition-all',
            activeRole === r
              ? 'bg-white text-text-primary shadow-sm'
              : 'text-text-secondary',
          )}
        >
          {r === 'member' ? <PersonIcon /> : <CrownIcon />}
        </button>
      ))}
    </div>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="rounded-[18px] p-4 bg-white/40 border border-white/60 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="w-[44px] h-[44px] rounded-[12px] bg-black/10 flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-black/10 rounded-full w-3/5" />
          <div className="h-3 bg-black/[0.06] rounded-full w-2/5" />
        </div>
        <div className="h-4 bg-black/10 rounded-full w-16" />
      </div>
    </div>
  );
}

// ─── Member Summary ──────────────────────────────────────────────────────────

function TotalDebtCard({
  totalDebt,
  currency,
  nearest,
}: {
  totalDebt: number;
  currency: string;
  nearest: string | null;
}) {
  const isClear = totalDebt <= 0;
  const t = useT();

  return (
    <GlassCard level="hero" className="rounded-[24px] p-5">
      <p className="text-[14px] text-text-secondary">
        {isClear ? t('home.totalDebt.clearTitle') : t('home.totalDebt.dueTitle')}
      </p>
      <MonoAmount
        value={amountText(totalDebt, currency)}
        variant={isClear ? 'paid' : 'debt'}
        size="xl"
        className="text-[42px] leading-[1.05] mt-1"
      />
      <div className="flex items-center gap-[7px] mt-2 text-[13px] font-medium text-status-warn">
        <span className={cn('w-[7px] h-[7px] rounded-full', isClear ? 'bg-status-paid' : 'bg-status-warn')} />
        {isClear
          ? t('home.totalDebt.clearHint')
          : nearest
            ? t('home.totalDebt.nearestDeadline', { date: nearest })
            : t('home.totalDebt.periodDebt')}
      </div>
    </GlassCard>
  );
}

function CollectionCard({
  sub,
  amountDue,
  onOpen,
}: {
  sub: Subscription;
  amountDue: number;
  onOpen: () => void;
}) {
  const letter = sub.name.trim()[0]?.toUpperCase() ?? '?';
  const t = useT();

  return (
    <GlassCard
      level="content"
      className="rounded-[20px] p-[15px] active:scale-[0.985] transition-transform"
      onClick={onOpen}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-[9px] min-w-0">
          <SubscriptionIcon letter={letter} size="md" />
          <b className="text-[16px] truncate">{sub.name}</b>
        </div>
        <span className="text-[11px] font-semibold text-status-warn bg-[rgba(230,103,53,0.15)] border border-[rgba(230,103,53,0.3)] rounded-full px-[9px] py-[3px]">
          {t('home.collectionBadge')}
        </span>
      </div>
      <MonoAmount
        value={amountText(amountDue, sub.currency)}
        variant={amountDue > 0 ? 'neutral' : 'paid'}
        size="lg"
        className="mt-3 block"
      />
    </GlassCard>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────

function EmptyState({ role }: { role: 'member' | 'holder' }) {
  const navigate = useNavigate();
  const t = useT();
  return (
    <div className="flex flex-col items-center gap-4 py-12 text-center">
      <span className="text-[48px]">{role === 'holder' ? '📋' : '🔍'}</span>
      <div>
        <p className="text-[16px] font-semibold text-text-primary">
          {role === 'holder' ? t('home.empty.holderTitle') : t('home.empty.memberTitle')}
        </p>
        <p className="text-[13px] text-text-secondary mt-1">
          {role === 'holder'
            ? t('home.empty.holderText')
            : t('home.empty.memberText')}
        </p>
      </div>
      {role === 'holder' && (
        <button
          className="mt-2 bg-brand-gradient text-white font-semibold text-[15px] px-6 py-3 rounded-[14px] shadow-[0_8px_18px_rgba(18,120,90,0.3)]"
          onClick={() => navigate('/create')}
        >
          {t('home.empty.create')}
        </button>
      )}
    </div>
  );
}

// ─── Holder View ──────────────────────────────────────────────────────────────

function HolderView({
  subs,
  pendingPayments,
  onDeleteDraft,
  deletingDraftId,
}: {
  subs: Subscription[];
  pendingPayments: PendingPaymentAttention[];
  onDeleteDraft: (id: string) => void;
  deletingDraftId: string | null;
}) {
  const t = useT();
  const holderSubs = subs.filter((s) => s.status !== 'draft' && s.status !== 'closed' && s.status !== 'archived');
  const draftSubs = subs.filter((s) => s.status === 'draft');

  if (holderSubs.length === 0 && draftSubs.length === 0) {
    return <EmptyState role="holder" />;
  }

  const totalOwed = holderSubs
    .reduce((sum, s) => sum + Number(s.total_price), 0)
    .toLocaleString('ru');

  return (
    <div className="flex flex-col gap-3">
      {pendingPayments.length > 0 && (
        <>
          <p className="text-[13px] font-semibold text-status-warn px-1">
            {t('home.holder.attention')}
          </p>
          {pendingPayments.map(({ claim, subscriptionName }) => (
            <PaymentConfirmation
              key={claim.id}
              claim={claim}
              subscriptionName={subscriptionName}
              compact
            />
          ))}
        </>
      )}

      {/* Total owed summary */}
      {holderSubs.length > 0 && (
        <div className="rounded-[18px] p-4 bg-brand-green/8 border border-brand-green/25 backdrop-blur-[8px]">
          <p className="text-[12px] text-text-secondary font-medium uppercase tracking-wide">{t('home.holder.totalMonthly')}</p>
          <p className="text-[32px] font-bold bg-brand-gradient bg-clip-text text-transparent leading-tight mt-1">
            {totalOwed} ₽
          </p>
          <p className="text-[12px] text-text-secondary mt-0.5">
            {t('home.holder.subscriptionsCount', { count: holderSubs.length })}
          </p>
        </div>
      )}

      {/* Drafts */}
      {draftSubs.length > 0 && (
        <>
          <p className="text-[13px] font-semibold text-text-secondary px-1">{t('home.holder.drafts')}</p>
          {draftSubs.map((s) => (
            <DraftCard
              key={s.id}
              sub={s}
              onDelete={onDeleteDraft}
              deleting={deletingDraftId === s.id}
            />
          ))}
        </>
      )}

      {/* Active subscriptions */}
      {holderSubs.length > 0 && (
        <>
          {draftSubs.length > 0 && (
            <p className="text-[13px] font-semibold text-text-secondary px-1 mt-1">{t('home.holder.active')}</p>
          )}
          {holderSubs.map((s) => <SubscriptionCardHolder key={s.id} sub={s} />)}
        </>
      )}
    </div>
  );
}

// ─── Member View ──────────────────────────────────────────────────────────────

function MemberView({
  subs,
  detailsById,
  localPayments,
  onOpen,
  onPay,
}: {
  subs: Subscription[];
  detailsById: Map<string, SubscriptionDetail>;
  localPayments: Record<string, LocalPaymentState>;
  onOpen: (sub: Subscription) => void;
  onPay: (sub: Subscription) => void;
}) {
  const t = useT();
  const lang = useI18nStore((s) => s.lang);
  if (subs.length === 0) {
    return <EmptyState role="member" />;
  }

  const subscriptions = subs.filter((s) => s.type !== 'collection');
  const collections = subs.filter((s) => s.type === 'collection');
  const totalDebt = subs.reduce(
    (sum, sub) => sum + resolveAmountDue(sub, detailsById.get(sub.id), localPayments[sub.id]),
    0,
  );
  const currency = subs[0]?.currency ?? 'RUB';

  return (
    <div className="flex flex-col gap-3">
      <TotalDebtCard totalDebt={totalDebt} currency={currency} nearest={nearestDeadline(subs, lang)} />

      {subscriptions.length > 0 && <SectionLabel>{t('home.member.subscriptions')}</SectionLabel>}
      {subscriptions.map((s) => {
        const amountDue = resolveAmountDue(s, detailsById.get(s.id), localPayments[s.id]);
        return (
          <SubscriptionCardMember
            key={s.id}
            sub={s}
            amountDue={amountDue}
            status={resolveMemberStatus(s, amountDue, localPayments[s.id])}
            onOpen={() => onOpen(s)}
            onPay={() => onPay(s)}
          />
        );
      })}

      {collections.length > 0 && <SectionLabel className="mt-1">{t('home.member.collections')}</SectionLabel>}
      {collections.map((s) => (
        <CollectionCard
          key={s.id}
          sub={s}
          amountDue={resolveAmountDue(s, detailsById.get(s.id), localPayments[s.id])}
          onOpen={() => onOpen(s)}
        />
      ))}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function HomePage() {
  const { activeRole, setRole } = useAppStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const t = useT();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [payingSub, setPayingSub] = useState<Subscription | null>(null);
  const [localPayments, setLocalPayments] = useState<Record<string, LocalPaymentState>>({});
  const [deletingDraftId, setDeletingDraftId] = useState<string | null>(null);

  useEffect(() => {
    const roleParam = searchParams.get('role');
    if ((roleParam === 'holder' || roleParam === 'member') && roleParam !== activeRole) {
      setRole(roleParam);
    }
  }, [activeRole, searchParams, setRole]);

  useEffect(() => {
    const roleParam = searchParams.get('role');
    if (roleParam === activeRole) return;
    if (roleParam === 'holder' || roleParam === 'member') return;
    const next = new URLSearchParams(searchParams);
    next.set('role', activeRole);
    setSearchParams(next, { replace: true });
  }, [activeRole, searchParams, setSearchParams]);

  const meQ = useQuery({
    queryKey: ['me'],
    queryFn: getCurrentUser,
  });

  const holderQ = useQuery({
    queryKey: ['subscriptions', 'holder'],
    queryFn: () => subscriptionApi.list('holder'),
  });

  const memberQ = useQuery({
    queryKey: ['subscriptions', 'participant'],
    queryFn: () => subscriptionApi.list('participant'),
  });

  const holderSubsForAttention = activeRole === 'holder'
    ? (holderQ.data?.items ?? []).filter((sub) => sub.status === 'active')
    : [];

  const holderHistoryQs = useQueries({
    queries: holderSubsForAttention.map((sub) => ({
      queryKey: ['subscription-history', sub.id, 'attention'],
      queryFn: () => ledgerApi.listHistory(sub.id, { limit: 20 }),
      enabled: activeRole === 'holder',
      staleTime: 15_000,
    })),
  });

  const pendingClaimIds = Array.from(new Set(
    holderHistoryQs.flatMap((query) => (
      query.data?.items
        .filter((event) => event.event_type === 'payment_marked')
        .map((event) => paymentClaimIdFromPayload(event.payload))
        .filter((id): id is string => Boolean(id)) ?? []
    )),
  ));

  const pendingClaimQs = useQueries({
    queries: pendingClaimIds.map((claimId) => ({
      queryKey: ['holder-payment-claim', claimId],
      queryFn: () => paymentApi.get(claimId),
      enabled: activeRole === 'holder',
      staleTime: 10_000,
    })),
  });

  const memberSubs = memberQ.data?.items ?? [];
  const memberDetailQs = useQueries({
    queries: memberSubs.map((sub) => ({
      queryKey: ['subscription', sub.id],
      queryFn: () => subscriptionApi.getDetail(sub.id),
      enabled: activeRole === 'member',
      staleTime: 20_000,
    })),
  });

  const detailsById = useMemo(() => {
    const map = new Map<string, SubscriptionDetail>();
    memberSubs.forEach((sub, index) => {
      const detail = memberDetailQs[index]?.data;
      if (detail) map.set(sub.id, detail);
    });
    return map;
  }, [memberDetailQs, memberSubs]);

  const isLoading = activeRole === 'holder' ? holderQ.isLoading : memberQ.isLoading;
  const isError   = activeRole === 'holder' ? holderQ.isError   : memberQ.isError;
  const refetch   = activeRole === 'holder' ? holderQ.refetch   : memberQ.refetch;
  const subs = activeRole === 'holder'
    ? (holderQ.data?.items ?? [])
    : (memberQ.data?.items ?? []);
  const pendingPayments: PendingPaymentAttention[] = pendingClaimQs
    .map((query) => query.data)
    .filter((claim): claim is PaymentClaim => claim?.status === 'awaiting_holder')
    .map((claim) => ({
      claim,
      subscriptionName: holderSubsForAttention.find((sub) => sub.id === claim.subscription_id)?.name
        ?? t('subscription.title'),
    }));
  const payingAmount = payingSub
    ? resolveAmountDue(payingSub, detailsById.get(payingSub.id), localPayments[payingSub.id])
    : 0;

  function handleRecorded(result: RecordPaymentResult) {
    if (!payingSub) return;
    const state: LocalPaymentState = result.ledger_operation_id ? 'paid' : 'pending';
    setLocalPayments((prev) => ({ ...prev, [payingSub.id]: state }));
    void qc.invalidateQueries({ queryKey: ['subscriptions', 'participant'] });
    void qc.invalidateQueries({ queryKey: ['subscription', payingSub.id] });
  }

  const deleteDraftMutation = useMutation({
    mutationFn: (id: string) => subscriptionApi.deleteDraft(id),
    onMutate: (id) => {
      setDeletingDraftId(id);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['subscriptions', 'holder'] });
    },
    onSettled: () => {
      setDeletingDraftId(null);
    },
  });

  return (
    <>
      <Page>
        <div className="px-[18px] pt-2 pb-6 flex flex-col gap-4">
          {meQ.data && (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[14px] text-text-secondary">{t('home.member.welcomeBack')}</p>
                <p className="text-[27px] font-bold tracking-[-0.01em]">
                  {meQ.data.first_name}
                </p>
              </div>
              <RoleToggle />
            </div>
          )}

          {isLoading && (
            <div className="flex flex-col gap-3">
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </div>
          )}

          {isError && (
            <div className="flex flex-col items-center gap-3 py-10 text-center">
              <p className="text-[15px] text-text-secondary">{t('home.member.loadError')}</p>
              <button
                className="text-brand-green font-semibold text-[14px]"
                onClick={() => refetch()}
              >
                {t('common.retry')}
              </button>
            </div>
          )}

          {!isLoading && !isError && (
            activeRole === 'holder'
              ? (
                <HolderView
                  subs={subs}
                  pendingPayments={pendingPayments}
                  onDeleteDraft={(id) => deleteDraftMutation.mutate(id)}
                  deletingDraftId={deletingDraftId}
                />
              )
              : (
                <MemberView
                  subs={subs}
                  detailsById={detailsById}
                  localPayments={localPayments}
                  onOpen={(sub) => navigate(`/subscriptions/${sub.id}`)}
                  onPay={(sub) => setPayingSub(sub)}
                />
              )
          )}
        </div>
      </Page>
      <BottomDock />
      <MarkPaidSheet
        open={Boolean(payingSub)}
        subscription={payingSub}
        amountDue={payingAmount}
        onClose={() => setPayingSub(null)}
        onRecorded={handleRecorded}
        onShowBalance={() => {
          if (payingSub) navigate(`/subscriptions/${payingSub.id}`);
          setPayingSub(null);
        }}
      />
    </>
  );
}
